import React from "react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, actions, eyebrow }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
                {eyebrow && (
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        {eyebrow}
                    </div>
                )}
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                    {title}
                </h1>
                {subtitle && (
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        {subtitle}
                    </p>
                )}
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
    );
}

export function StatusChip({ status, label, className }) {
    const map = {
        clean: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.25)]",
        dirty: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.3)]",
        cleaning: "bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))] border-[hsl(var(--info)/0.25)]",
        inspected: "bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))] border-[hsl(var(--info)/0.25)]",
        out_of_order: "bg-[hsl(var(--destructive)/0.10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.25)]",
        occupied: "bg-[hsl(var(--primary)/0.10)] text-[hsl(var(--primary))] border-[hsl(var(--primary)/0.22)]",
        reserved: "bg-[hsl(var(--primary)/0.10)] text-[hsl(var(--primary))] border-[hsl(var(--primary)/0.22)]",
        checked_in: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.25)]",
        checked_out: "bg-secondary text-muted-foreground border-border",
        cancelled: "bg-[hsl(var(--destructive)/0.10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.25)]",
        no_show: "bg-[hsl(var(--destructive)/0.10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.25)]",
        connected: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.25)]",
        available: "bg-secondary text-muted-foreground border-border",
        error: "bg-[hsl(var(--destructive)/0.10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.25)]",
        positive: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.25)]",
        neutral: "bg-secondary text-foreground border-border",
        needs_attention: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.3)]",
        urgent: "bg-[hsl(var(--destructive)/0.10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.25)]",
        new: "bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))] border-[hsl(var(--info)/0.25)]",
        assigned: "bg-secondary text-foreground border-border",
        in_progress: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.3)]",
        waiting: "bg-secondary text-muted-foreground border-border",
        done: "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.25)]",
        low: "bg-secondary text-muted-foreground border-border",
        med: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.3)]",
        normal: "bg-secondary text-muted-foreground border-border",
        high: "bg-[hsl(var(--destructive)/0.10)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.25)]",
    };
    const cls = map[status] || "bg-secondary text-foreground border-border";
    const text = label || (status ? status.replace(/_/g, " ") : "\u2014");
    return (
        <span
            data-testid="status-chip"
            className={cn(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium border capitalize",
                cls,
                className,
            )}
        >
            {text}
        </span>
    );
}

export function EmptyState({ title, hint, action }) {
    return (
        <div
            className="rounded-xl border border-border bg-card p-10 text-center"
            data-testid="empty-state"
        >
            <div className="text-sm font-medium">{title}</div>
            {hint && (
                <div className="text-xs text-muted-foreground mt-1">{hint}</div>
            )}
            {action && <div className="mt-3 inline-block">{action}</div>}
        </div>
    );
}

export function LoadingBlock({ rows = 3 }) {
    return (
        <div className="space-y-2" data-testid="loading-skeleton">
            {Array.from({ length: rows }).map((_, i) => (
                <div
                    key={i}
                    className="h-10 rounded-lg bg-secondary animate-pulse"
                />
            ))}
        </div>
    );
}
